// Unit preferences (owner spec, 2026-08-09). Storage is ALWAYS canonical —
// kg, cm, kcal, g/ml, mg/dL — and conversion happens only at display and
// input, so changing a preference can never corrupt existing data. Factors
// are exact where a definition exists; kcal→kJ and the glucose divisor are
// the owner-specified values.

export interface UnitPrefs {
  weight: "kg" | "lb";
  length: "cm" | "in";
  energy: "kcal" | "kj";
  /** Governs solids g↔oz and liquids ml↔fl oz together; mg stays mg. */
  serving: "metric" | "imperial";
  glucose: "mgdl" | "mmol";
}

/** Metric-first defaults (DACH home market). */
export const DEFAULT_UNITS: UnitPrefs = {
  weight: "kg", length: "cm", energy: "kcal", serving: "metric", glucose: "mgdl",
};

export const KJ_PER_KCAL = 4.184;
export const LB_PER_KG = 2.2046226218;
export const CM_PER_IN = 2.54;
export const G_PER_OZ = 28.349523125;
export const ML_PER_FLOZ = 29.5735295625;
export const GLUCOSE_MGDL_PER_MMOL = 18.0182;

// ── canonical → display ──
export const kgToLb = (kg: number): number => kg * LB_PER_KG;
export const cmToIn = (cm: number): number => cm / CM_PER_IN;
export const kcalToKj = (kcal: number): number => kcal * KJ_PER_KCAL;
export const gToOz = (g: number): number => g / G_PER_OZ;
export const mlToFloz = (ml: number): number => ml / ML_PER_FLOZ;
export const mgdlToMmol = (mgdl: number): number => mgdl / GLUCOSE_MGDL_PER_MMOL;

// ── display → canonical ──
export const lbToKg = (lb: number): number => lb / LB_PER_KG;
export const inToCm = (inches: number): number => inches * CM_PER_IN;
export const kjToKcal = (kj: number): number => kj / KJ_PER_KCAL;
export const ozToG = (oz: number): number => oz * G_PER_OZ;
export const flozToMl = (floz: number): number => floz * ML_PER_FLOZ;
export const mmolToMgdl = (mmol: number): number => mmol * GLUCOSE_MGDL_PER_MMOL;

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** Body weight for display: 1 decimal in either unit. */
export function displayWeight(kg: number, u: UnitPrefs): { value: number; unit: "kg" | "lb" } {
  return u.weight === "lb" ? { value: round1(kgToLb(kg)), unit: "lb" } : { value: round1(kg), unit: "kg" };
}
export function parseWeightInput(value: number, u: UnitPrefs): number {
  return u.weight === "lb" ? lbToKg(value) : value;
}

/** Height for display: whole cm, or inches to 1 decimal. */
export function displayLength(cm: number, u: UnitPrefs): { value: number; unit: "cm" | "in" } {
  return u.length === "in" ? { value: round1(cmToIn(cm)), unit: "in" } : { value: Math.round(cm), unit: "cm" };
}
export function parseLengthInput(value: number, u: UnitPrefs): number {
  return u.length === "in" ? inToCm(value) : value;
}

/** Energy for display: whole numbers in either unit. */
export function displayEnergy(kcal: number, u: UnitPrefs): { value: number; unit: "kcal" | "kJ" } {
  return u.energy === "kj"
    ? { value: Math.round(kcalToKj(kcal)), unit: "kJ" }
    : { value: Math.round(kcal), unit: "kcal" };
}

/** Solid amounts: whole g, or oz to 1 decimal. */
export function displayServing(g: number, u: UnitPrefs): { value: number; unit: "g" | "oz" } {
  return u.serving === "imperial" ? { value: round1(gToOz(g)), unit: "oz" } : { value: Math.round(g), unit: "g" };
}

/** Liquid amounts: whole ml, or fl oz to 1 decimal. */
export function displayVolume(ml: number, u: UnitPrefs): { value: number; unit: "ml" | "fl oz" } {
  return u.serving === "imperial"
    ? { value: round1(mlToFloz(ml)), unit: "fl oz" }
    : { value: Math.round(ml), unit: "ml" };
}

/** Blood glucose: whole mg/dL, or mmol/L to 1 decimal. */
export function displayGlucose(mgdl: number, u: UnitPrefs): { value: number; unit: "mg/dL" | "mmol/L" } {
  return u.glucose === "mmol"
    ? { value: round1(mgdlToMmol(mgdl)), unit: "mmol/L" }
    : { value: Math.round(mgdl), unit: "mg/dL" };
}

/** Parse a stored/client-sent prefs value into a guaranteed-valid object —
 *  unknown keys dropped, invalid values fall back to the default. */
export function sanitizeUnitPrefs(raw: unknown): UnitPrefs {
  const r = (typeof raw === "string" ? safeParse(raw) : raw) as Partial<UnitPrefs> | null;
  const pick = <K extends keyof UnitPrefs>(k: K, allowed: readonly UnitPrefs[K][]): UnitPrefs[K] =>
    r && allowed.includes(r[k] as UnitPrefs[K]) ? (r[k] as UnitPrefs[K]) : DEFAULT_UNITS[k];
  return {
    weight: pick("weight", ["kg", "lb"] as const),
    length: pick("length", ["cm", "in"] as const),
    energy: pick("energy", ["kcal", "kj"] as const),
    serving: pick("serving", ["metric", "imperial"] as const),
    glucose: pick("glucose", ["mgdl", "mmol"] as const),
  };
}

function safeParse(s: string): unknown {
  try { return JSON.parse(s); } catch { return null; }
}
